/**
 * `DevQuickSignIn` — localhost-only one-click sign-in helpers for the
 * Clerk-hosted form during local dev.
 *
 * Two contextual buttons (URL-aware):
 *   - On `/sign-in`:                "Sign in with {name}" — fills the
 *                                   email input + clicks Continue.
 *   - On `/sign-in/factor-one`:     injects whichever credential Clerk is
 *                                   actually asking for + clicks Continue.
 *                                   **Factor-one is not always the OTP:** a
 *                                   user whose invitation was accepted
 *                                   SERVER-SIDE is created WITH a password,
 *                                   so Clerk asks for the password and the
 *                                   code input never mounts. The button
 *                                   detects which input is present rather
 *                                   than assuming, and labels itself to
 *                                   match. Assuming OTP is what used to
 *                                   hang it on "Injecting…" until timeout.
 *                                   Values: Clerk's fixed test-mode code
 *                                   (ai-docs/login-credentials.md §A.1 /
 *                                   §C.1) and lexserver's
 *                                   `INVITATION_TEST_PASSWORD`.
 *
 * Explicit two-step rather than auto-walk because Clerk's factor-one
 * mounts the OTP input slightly before its `prepareFirstFactor`
 * handshake binds React state — a fire-and-forget walk races that
 * binding. Letting the operator drive each step removes the race
 * without sacrificing speed (one extra click).
 *
 * Renders only when BOTH `import.meta.env.DEV` is true (production
 * builds tree-shake the body) AND the hostname is localhost/127.0.0.1
 * (defense in depth against any stray dev build on a non-local host).
 *
 * No Clerk SDK calls — drives the exact same DOM the operator would
 * type into manually, so every downstream auth concern (auth-exchange,
 * wrong-app-session detection, runtime provisioning) walks the
 * production path unchanged.
 */

import { useEffect, useState } from "react";

export interface QuickSignInUser {
  /** Display label rendered as `Sign in with {label}` (e.g. "lexdemo"). */
  label: string;
  /** Full Clerk identifier — typically a `+clerk_test@…` email. */
  identifier: string;
}

interface DevQuickSignInProps {
  users: QuickSignInUser[];
}

const TEST_MODE_CODE = "424242";

/** Clerk's factor-one is NOT always the email OTP. A user whose invitation
 *  was accepted SERVER-SIDE (`acceptInvitationServerSide`, the operator
 *  unblock path + the provisioning auto-accept step) is created WITH a
 *  password, so Clerk offers **password** as the first factor and the OTP
 *  input never mounts — the old code-only injector hung on
 *  `waitForStableElement` until it timed out.
 *
 *  Mirrors `lexserver/src/clerk/invitations.ts` `INVITATION_TEST_PASSWORD`.
 *  Dev-only test credential for `+clerk_test` identities; it is not a
 *  secret and never reaches a production build (this whole component is
 *  DEV + localhost gated). */
const TEST_MODE_PASSWORD = "Lexiun-424242";

const CODE_SELECTOR = 'input[name="code"], input[inputmode="numeric"]';
const PASSWORD_SELECTOR = 'input[name="password"], input[type="password"]';

function isLocalhost(): boolean {
  if (typeof window === "undefined") return false;
  const h = window.location.hostname;
  return h === "localhost" || h === "127.0.0.1";
}

/** Lightweight pathname watcher. Clerk's `routing="path"` SignIn uses
 *  `history.pushState` to advance between steps, which doesn't trigger
 *  React re-renders on its own — so we poll. 200ms is plenty for a
 *  dev tool. */
function useLocationPath(): string {
  const [path, setPath] = useState(() =>
    typeof window === "undefined" ? "/" : window.location.pathname,
  );
  useEffect(() => {
    const id = setInterval(() => {
      if (window.location.pathname !== path) setPath(window.location.pathname);
    }, 200);
    return () => clearInterval(id);
  }, [path]);
  return path;
}

async function waitForElement<T extends Element>(
  selector: string,
  timeoutMs = 8000,
): Promise<T> {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    const el = document.querySelector<T>(selector);
    if (el) return el;
    await new Promise((r) => setTimeout(r, 100));
  }
  throw new Error(`timed out waiting for ${selector}`);
}

/** Wait for the SAME element matching `selector` to remain in the DOM
 *  continuously for `stableMs`. Used to ride out the email→OTP screen
 *  flicker (lexide's auth-exchange / RequireOrgUser bounce can remount
 *  the factor-one form once or twice before it settles). */
async function waitForStableElement<T extends Element>(
  selector: string,
  stableMs: number,
  timeoutMs: number,
): Promise<T> {
  const deadline = Date.now() + timeoutMs;
  let lastEl: T | null = null;
  let stableSince = 0;
  while (Date.now() < deadline) {
    const el = document.querySelector<T>(selector);
    if (el && el === lastEl && document.contains(el)) {
      if (Date.now() - stableSince >= stableMs) return el;
    } else {
      lastEl = el;
      stableSince = Date.now();
    }
    await new Promise((r) => setTimeout(r, 100));
  }
  throw new Error(`timed out waiting for ${selector} to be stable`);
}

/** Which credential factor-one is actually asking for. Resolved by LOOKING
 *  rather than assumed, because it depends on how that user's Clerk identity
 *  was created (server-side accept ⇒ password; plain invite ⇒ email OTP). */
type FactorKind = "code" | "password" | null;

function detectFactorKind(): FactorKind {
  if (document.querySelector(PASSWORD_SELECTOR)) return "password";
  if (document.querySelector(CODE_SELECTOR)) return "code";
  return null;
}

/** Wait until factor-one settles on EITHER credential input, and report
 *  which. Racing both is the whole point: committing to one selector up
 *  front is what made the old injector hang on password-first users. */
async function waitForEitherStable(
  stableMs: number,
  timeoutMs: number,
): Promise<{ kind: Exclude<FactorKind, null>; el: HTMLInputElement }> {
  const deadline = Date.now() + timeoutMs;
  let lastEl: HTMLInputElement | null = null;
  let stableSince = 0;
  while (Date.now() < deadline) {
    const kind = detectFactorKind();
    const el = kind
      ? document.querySelector<HTMLInputElement>(
          kind === "password" ? PASSWORD_SELECTOR : CODE_SELECTOR,
        )
      : null;
    if (el && kind && el === lastEl && document.contains(el)) {
      if (Date.now() - stableSince >= stableMs) return { kind, el };
    } else {
      lastEl = el;
      stableSince = Date.now();
    }
    await new Promise((r) => setTimeout(r, 100));
  }
  throw new Error(
    "timed out waiting for factor-one to show a code or password input",
  );
}

/** Set an input's value through the React-aware native setter so React's
 *  onChange + Clerk's controlled state actually see the update. */
function setReactInputValue(input: HTMLInputElement, value: string): void {
  const proto = Object.getPrototypeOf(input);
  const setter = Object.getOwnPropertyDescriptor(proto, "value")?.set;
  if (!setter) input.value = value;
  else setter.call(input, value);
  input.dispatchEvent(new Event("input", { bubbles: true }));
  input.dispatchEvent(new Event("change", { bubbles: true }));
}

/** Type a string into an input one character at a time with realistic
 *  delays + full keystroke events. Clerk's OTP input has bot-detection
 *  heuristics that silently reject instant value-sets; per-character
 *  typing with keydown/keypress/input/keyup events looks like a real
 *  user and gets through. */
async function typeIntoInput(input: HTMLInputElement, text: string): Promise<void> {
  input.focus();
  for (const ch of text) {
    input.dispatchEvent(
      new KeyboardEvent("keydown", { key: ch, bubbles: true, cancelable: true }),
    );
    input.dispatchEvent(
      new KeyboardEvent("keypress", { key: ch, bubbles: true, cancelable: true }),
    );
    setReactInputValue(input, input.value + ch);
    input.dispatchEvent(
      new KeyboardEvent("keyup", { key: ch, bubbles: true, cancelable: true }),
    );
    // Realistic-ish keystroke cadence — humans type ~60-100ms/char.
    await new Promise((r) => setTimeout(r, 70 + Math.random() * 40));
  }
}

function clickPrimary(form: HTMLFormElement | null): boolean {
  if (!form) return false;
  const primary =
    form.querySelector<HTMLButtonElement>(
      'button[data-localization-key="formButtonPrimary"]',
    ) ??
    form.querySelector<HTMLButtonElement>('button[type="submit"]') ??
    form.querySelector<HTMLButtonElement>("button:not([disabled])");
  if (!primary) return false;
  primary.click();
  return true;
}

export function DevQuickSignIn(props: DevQuickSignInProps) {
  if (!import.meta.env.DEV) return null;
  if (!isLocalhost()) return null;
  return <DevQuickSignInInner {...props} />;
}

function DevQuickSignInInner({ users }: DevQuickSignInProps) {
  const path = useLocationPath();
  const onFactorOne = path.includes("/sign-in/factor");
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Label what the button will ACTUALLY do. Which factor Clerk asks for
  // depends on how the identity was created, so it is observed, not assumed.
  const [factorKind, setFactorKind] = useState<FactorKind>(null);
  useEffect(() => {
    if (!onFactorOne) {
      setFactorKind(null);
      return;
    }
    const id = setInterval(() => setFactorKind(detectFactorKind()), 200);
    return () => clearInterval(id);
  }, [onFactorOne]);

  async function fillEmailAndSubmit(identifier: string) {
    setError(null);
    setBusy(identifier);
    try {
      const emailInput = await waitForElement<HTMLInputElement>(
        'input[name="identifier"]',
      );
      setReactInputValue(emailInput, identifier);
      await new Promise((r) => setTimeout(r, 50));
      if (!clickPrimary(emailInput.closest("form"))) {
        throw new Error("Couldn't find the Continue button on the email step");
      }
    } catch (e: unknown) {
      setError((e as Error)?.message ?? String(e));
    } finally {
      setBusy(null);
    }
  }

  async function injectCredentialAndSubmit() {
    setError(null);
    setBusy("__code__");
    try {
      // The post-`8b31f781` `RequireOrgUser` + `auth-exchange ORG_DELETED`
      // silent-signOut paths cause a visible flicker between email-step
      // submit and factor-one settling — the DOM may remount once or twice
      // during that window. Wait until the same input has been continuously
      // attached for ~1s before typing, so we don't fire keystrokes against
      // a node that's about to be unmounted.
      const { kind, el } = await waitForEitherStable(1000, 12000);
      // Type character-by-character (with timing + key events) so Clerk's
      // bot-detection heuristics don't silently reject the value — it
      // rejects instant value-sets on the OTP field.
      await typeIntoInput(el, kind === "password" ? TEST_MODE_PASSWORD : TEST_MODE_CODE);
      // Clerk auto-submits once an OTP is fully populated; the password form
      // needs the click. Harmless on both.
      await new Promise((r) => setTimeout(r, 100));
      clickPrimary(el.closest("form"));
    } catch (e: unknown) {
      setError((e as Error)?.message ?? String(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div
      data-testid="dev-quick-signin"
      style={{
        width: "100%",
        maxWidth: 400,
        padding: "10px 12px 12px",
        borderRadius: 6,
        background: "rgba(212,160,82,0.06)",
        border: "1px dashed rgba(212,160,82,0.45)",
        fontFamily: "'DM Sans', system-ui, sans-serif",
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          fontSize: "0.6875rem",
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: "#D4A052",
          marginBottom: 8,
        }}
      >
        Dev quick sign-in · localhost only
      </div>
      {onFactorOne ? (
        <button
          type="button"
          onClick={injectCredentialAndSubmit}
          disabled={busy != null}
          data-testid="dev-quick-signin-code"
          style={{
            fontSize: "0.8125rem",
            padding: "6px 10px",
            borderRadius: 4,
            border: "1px solid rgba(212,160,82,0.4)",
            background: "rgba(212,160,82,0.1)",
            color: "#F2EEE9",
            cursor: busy != null ? "default" : "pointer",
          }}
        >
          {busy === "__code__"
            ? "Injecting…"
            : factorKind === "password"
              ? "Inject test password"
              : factorKind === "code"
                ? "Inject 424242 code"
                : "Inject test credential"}
        </button>
      ) : null}
      {/* Explain the password screen IN PLACE. It surprises everyone the
          first time ("where is the 424242 box?"), and the explanation is
          only useful at the exact moment it is confusing. Documented at
          ai-docs/login-credentials.md §C.3. */}
      {onFactorOne && factorKind === "password" && (
        <div
          data-testid="dev-quick-signin-password-note"
          style={{
            marginTop: 8,
            fontSize: "0.6875rem",
            lineHeight: 1.45,
            color: "#C9BFB2",
          }}
        >
          Clerk is asking for a <strong>password</strong>, not the 424242 code —
          this account’s invitation was accepted server-side, so it was created
          with one. Password is <em>{"Lexiun-424242"}</em>. They are alternative
          first factors, not two steps; “Use another method” switches. See
          login-credentials.md §C.3.
        </div>
      )}
      {!onFactorOne && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {users.map((u) => (
            <button
              key={u.identifier}
              type="button"
              onClick={() => fillEmailAndSubmit(u.identifier)}
              disabled={busy != null}
              data-testid={`dev-quick-signin-${u.label}`}
              style={{
                fontSize: "0.8125rem",
                padding: "6px 10px",
                borderRadius: 4,
                border: "1px solid rgba(212,160,82,0.4)",
                background: "rgba(212,160,82,0.1)",
                color: "#F2EEE9",
                cursor: busy != null ? "default" : "pointer",
                opacity: busy != null && busy !== u.identifier ? 0.55 : 1,
              }}
            >
              {busy === u.identifier ? "Signing in…" : `Sign in with ${u.label}`}
            </button>
          ))}
        </div>
      )}
      {error && (
        <div
          role="alert"
          style={{
            marginTop: 8,
            fontSize: "0.75rem",
            color: "#ff8a8a",
            wordBreak: "break-word",
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
}
